import { StoreHour } from '@adapters/prismic/fetching/stores/types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export interface IsClosedArgs {
  open: string;
  close: string;
}

export const buildHoursMessaging = (
  userTime: Date,
  storeHours: StoreHour[],
  t: (
    args0: string,
    args1?: { time?: string; openTime?: string; closeTime?: string }
  ) => string
) => {
  if (!storeHours?.length) {
    return null;
  }
  let navigationMessage = '';
  let fullMessage = '';
  const today = storeHours[0];

  const tomorrow = storeHours[1];
  const openingTime = dayjs(today.open, 'h:ma');
  const closingTime = dayjs(today.close, 'h:ma');
  const isClosed = (hours: IsClosedArgs) => {
    if (hours?.open?.length === 0) {
      return true;
    }
    return ['closed', 'fermé', 'close', 'ferme'].includes(
      hours?.close?.trim().toLowerCase()
    );
  };

  if (isClosed(today) && isClosed(tomorrow)) {
    return {
      navigationMessage: t('header.storeInformation.closedToday'),
      fullMessage: `${t('header.storeInformation.closedToday')}. ${t(
        'header.storeInformation.closedTomorrow'
      )}`,
    };
  }

  if (isClosed(today)) {
    return {
      navigationMessage: t('header.storeInformation.closedToday'),
      fullMessage: `${t('header.storeInformation.closedToday')}. ${t(
        'header.storeInformation.openTomorrow',
        {
          time: tomorrow.open,
        }
      )}`,
    };
  }

  if (dayjs(userTime).isBefore(openingTime)) {
    navigationMessage = t('header.storeInformation.openAt', {
      time: today.open,
    });
    fullMessage = t('header.storeInformation.openToday', {
      openTime: today.open,
      closeTime: today.close,
    });
  } else if (
    dayjs(userTime).isAfter(openingTime) &&
    dayjs(userTime).isBefore(closingTime)
  ) {
    navigationMessage = t('header.storeInformation.openTill', {
      time: today.close,
    });
    fullMessage = t('header.storeInformation.openToday', {
      openTime: today.open,
      closeTime: today.close,
    });
  } else if (dayjs(userTime).isAfter(closingTime) && isClosed(tomorrow)) {
    navigationMessage = t('header.storeInformation.closedTomorrow');
    fullMessage = t('header.storeInformation.closedTomorrow');
  } else if (dayjs(userTime).isAfter(closingTime)) {
    navigationMessage = t('header.storeInformation.openTomorrow', {
      time: tomorrow.open,
    });
    fullMessage = t('header.storeInformation.openTomorrowToFrom', {
      openTime: tomorrow.open,
      closeTime: tomorrow.close,
    });
  } else {
    navigationMessage = t('header.storeInformation.openAt', {
      time: today.open,
    });
    fullMessage = t('header.storeInformation.openToday', {
      openTime: today.open,
      closeTime: today.close,
    });
  }

  return {
    fullMessage,
    navigationMessage,
  };
};

export function daysUntilNext(dayName: string, locale = 'en'): number {
  // Map day names to indices (Sunday = 0, Monday = 1, ..., Saturday = 6)
  const daysEn = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const daysFr = [
    'dimanche',
    'lundi',
    'mardi',
    'mercredi',
    'jeudi',
    'vendredi',
    'samedi',
  ];
  const days = locale === 'fr' ? daysFr : daysEn;

  const today = new Date();
  const todayIndex = today.getDay();
  const targetIndex = days.findIndex(
    (d) => d.toLowerCase() === dayName.toLowerCase()
  );

  if (targetIndex === -1) return -1; // Invalid day name

  let diff = (targetIndex - todayIndex + 7) % 7;
  if (diff === 0) diff = 7; // If today, return 7 (next week)

  return diff;
}
